import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';

import supportIssuesApi from '../../utils/supportIssuesApi';
import { supportIssueToViewModel, mapStatusCategoryToCard } from '../../utils/supportMappers';
import { PRIORITY_OPTIONS } from '../../types/support';
import { createNotification } from '../../utils/alerts';

import SupportHeader from '../../components/support/SupportHeader';
import SupportStatsCards from '../../components/support/SupportStatsCards';
import SupportFilters from '../../components/support/SupportFilters';
import SupportIssueGrid from '../../components/support/SupportIssueGrid';
import SupportIssueDetailsDialog from '../../components/support/SupportIssueDetailsDialog';
import SupportCreateIssueDialog from '../../components/support/SupportCreateIssueDialog';
import ResponsiveAdminPage from '../../components/admin/ResponsiveAdminPage';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

// O padding da tela é gerenciado pelo .admin-content (28px), não aqui.
export default function SupportPage() {
  const isMobile = useMediaQuery('(max-width:600px)');

  /* ── Estado ───────────────────────────────────────────────────────── */
  const [selectedStatus, setSelectedStatus] = useState(
    /** @type {import('../../types/support').SupportStatusFilter} */ ('Todos os chamados'),
  );
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);
  const [isLoadingIssues, setIsLoadingIssues] = useState(false);
  const [issuesError, setIssuesError] = useState(/** @type {string | null} */ (null));
  const [issues, setIssues] = useState(
    /** @type {import('../../types/support').SupportIssueViewModel[]} */ ([]),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('Todas');
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(
    /** @type {import('../../types/support').SupportIssueViewModel | null} */ (null),
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  /* ── Carregar chamados ────────────────────────────────────────────── */
  const fetchIssues = useCallback(async () => {
    setIsLoadingIssues(true);
    setIssuesError(null);
    try {
      const raw = await supportIssuesApi.fetchAllIssues();
      const vms = raw.map(supportIssueToViewModel).filter(Boolean);
      setIssues(vms);
    } catch (err) {
      if (err?.message !== 'Não autorizado') {
        setIssuesError('Erro ao buscar chamados');
        createNotification('error', 'Erro ao buscar chamados', isMobile);
      }
    } finally {
      setIsLoadingIssues(false);
    }
  }, [isMobile]);

  // Após criar um chamado, a tela precisa entrar em estado de carregamento
  // imediatamente (feedback instantâneo), mas a busca real só é disparada
  // depois de um pequeno atraso — dando tempo do backend/Jira processarem
  // o chamado recém-criado antes de repopular a lista.
  const fetchIssuesAfterCreate = useCallback(() => {
    setIsLoadingIssues(true);
    setIssuesError(null);
    setTimeout(() => {
      fetchIssues();
    }, 3000);
  }, [fetchIssues]);

  useEffect(() => {
    fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Filtros client-side ──────────────────────────────────────────── */
  const filteredIssues = issues.filter((issue) => {
    if (
      selectedStatus !== 'Todos os chamados' &&
      mapStatusCategoryToCard(issue.statusCategoryKey) !== selectedStatus
    ) {
      return false;
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const haystack = [issue.key, issue.title, issue.content, issue.statusName, issue.priority]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }

    if (selectedPriority && selectedPriority !== 'Todas') {
      if (issue.priority !== selectedPriority) return false;
    }

    if (selectedStartDate) {
      const start = new Date(`${selectedStartDate}T00:00:00`).getTime();
      const d = issue.createdAt ? new Date(issue.createdAt).getTime() : null;
      if (!d || d < start) return false;
    }

    if (selectedEndDate) {
      const end = new Date(`${selectedEndDate}T23:59:59.999`).getTime();
      const d = issue.createdAt ? new Date(issue.createdAt).getTime() : null;
      if (!d || d > end) return false;
    }

    return true;
  });

  /* ── Contadores (calculados sobre todos os chamados, sem filtros) ─── */
  const counts = {
    all: issues.length,
    waiting: issues.filter((i) => mapStatusCategoryToCard(i.statusCategoryKey) === 'Em espera').length,
    inProgress: issues.filter((i) => mapStatusCategoryToCard(i.statusCategoryKey) === 'Em andamento').length,
    done: issues.filter((i) => mapStatusCategoryToCard(i.statusCategoryKey) === 'Concluídos').length,
  };

  /* ── Handlers ─────────────────────────────────────────────────────── */
  function handleIssueChange(updatedIssue) {
    setSelectedIssue(updatedIssue);
    setIssues((prev) =>
      prev.map((i) =>
        i.issueExternalId === updatedIssue.issueExternalId ? updatedIssue : i,
      ),
    );
  }

  function handleCreated() {
    createNotification('success', 'Chamado criado com sucesso!', isMobile);
  }

  return (
    <ResponsiveAdminPage>
      <SupportHeader />

      <SupportFilters
        subtitle="Ajuda e suporte"
        extraActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setIsCreateDialogOpen(true)}
            sx={{
              backgroundColor: PRIMARY,
              '&:hover': { backgroundColor: PRIMARY_DARK },
              textTransform: 'none',
              boxShadow: 'none',
              fontWeight: 600,
            }}
          >
            Criar Chamado
          </Button>
        }
        collapsed={isFiltersCollapsed}
        searchTerm={searchTerm}
        selectedPriority={selectedPriority}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        priorityOptions={PRIORITY_OPTIONS}
        onToggleCollapsed={() => setIsFiltersCollapsed((p) => !p)}
        onSearchChange={setSearchTerm}
        onPriorityChange={setSelectedPriority}
        onStartDateChange={setSelectedStartDate}
        onEndDateChange={setSelectedEndDate}
      />

      <SupportStatsCards
        selectedStatus={selectedStatus}
        counts={counts}
        isLoading={isLoadingIssues}
        onStatusChange={setSelectedStatus}
      />

      <SupportIssueGrid
        issues={filteredIssues}
        isLoading={isLoadingIssues}
        error={issuesError}
        onSelectIssue={setSelectedIssue}
      />

      <SupportIssueDetailsDialog
        issue={selectedIssue}
        open={Boolean(selectedIssue)}
        onClose={() => setSelectedIssue(null)}
        onIssueChange={handleIssueChange}
      />

      <SupportCreateIssueDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreated={handleCreated}
        onReloadFallback={fetchIssuesAfterCreate}
      />
    </ResponsiveAdminPage>
  );
}
